import React from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import {
  FormField,
  FormSection,
  RequiredNote,
  SelectField,
  TextField,
} from '../../../../../Components/Form';
import { FOB_INCOTERM_ID, TIMELINE_OPTIONS } from '../shared/constants';
import { formatParcelSize } from '../shared/utils';
import {
  CounterProductAlert,
  CounterModalLoading,
  CounterModalFooter,
  CounterPortPick,
  CounterPortBlock,
  CounterDischargePorts,
  getCounterPortCount,
} from '../shared/counterOfferShared';
import {
  CARGO_RATIO_LABELS,
  getCargoRatioKey,
} from '../../../../../shared/offer-domain/cargoRatio';
import './CargoRatioSelector.css';
import '../shared/counterIncoterm.css';

const RATIO_OPTIONS = CARGO_RATIO_LABELS.map((label, index) => ({
  id: index + 1,
  label,
}));

const isRatioSelected = (selectedRatios, option) =>
  selectedRatios.some(
    ratio =>
      getCargoRatioKey(ratio.id) === getCargoRatioKey(option.id) ||
      (ratio.label && ratio.label === option.label)
  );

const CounterOfferModalLpg = ({
  isOpen,
  toggle,
  formData,
  onFormChange,
  onPortSetup,
  onSave,
  loading,
  saveDisabled = false,
  errors = {},
  form,
  requiresCargoRatio = false,
  incotermTypeOptions,
  incotermRouteOptions: _incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
}) => {
  const selectedRatios = formData.selectedRatios || [];

  const handleToggleRatio = option => {
    const ratioKey = getCargoRatioKey(option.id);
    const exists = selectedRatios.some(
      item => getCargoRatioKey(item.id) === ratioKey
    );
    const next = exists
      ? selectedRatios.filter(item => getCargoRatioKey(item.id) !== ratioKey)
      : [...selectedRatios, option];
    onFormChange('selectedRatios', next);
  };

  const isFobIncoterm = formData.incotermId === FOB_INCOTERM_ID;
  const portCount = getCounterPortCount(formData);

  const timelineLabel =
    TIMELINE_OPTIONS.find(option => option.value === formData.timelineType)
      ?.label || 'Accepted Discharged Dates';

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop='static'
      size='xl'
      scrollable
    >
      <ModalHeader toggle={toggle}>Add Counter Requirement (LPG)</ModalHeader>
      <ModalBody className='p-4'>
        <CounterProductAlert form={form} />

        {loading ? (
          <CounterModalLoading />
        ) : (
          <>
            <RequiredNote />

            <FormSection
              title='Delivery terms'
              description='Incoterm, ports and route'
              columns={isFobIncoterm ? 2 : 1}
            >
              <SelectField
                label='Type'
                name='incotermId'
                required
                options={incotermTypeOptions}
                value={formData.incotermId}
                onChange={value => onFormChange('incotermId', value ?? '')}
                error={errors.incotermId}
              />

              {isFobIncoterm ? (
                <SelectField
                  label='Route'
                  name='incotermRouteId'
                  required
                  options={fobRouteOptions}
                  value={formData.incotermRouteId}
                  onChange={value =>
                    onFormChange('incotermRouteId', value ?? '')
                  }
                  error={errors.incotermRouteId}
                />
              ) : null}
            </FormSection>

            <FormSection
              title='Quantity and timeline'
              description='Total volume and what the dates mean'
              columns={3}
            >
              <TextField
                label='Total quantity'
                name='totalQuantity'
                mode='calculated'
                value={formatParcelSize(formData.deliveryBasis, 'MT')}
                hint='Total of the delivery basis'
              />
              <TextField
                label='Timeline'
                name='timelineType'
                mode='readonly'
                value={timelineLabel}
              />
            </FormSection>

            {isFobIncoterm ? (
              <CounterPortBlock
                portNo={1}
                title='Loading'
                locationField='incotermLocationId'
                multiSelectField='incotermFOB'
                isMultiSelect
                multiSelectValue={formData.incotermFOB}
                dateFromField='startDate'
                dateFromValue={formData.startDate}
                dateToField='endDate'
                dateToValue={formData.endDate}
                dateLabel='Accepted loading dates'
                showQuantity={false}
                incotermLocationOptions={incotermLocationOptions}
                onFormChange={onFormChange}
                errors={errors}
              />
            ) : formData.incotermId ? (
              <>
                <CounterPortPick
                  portCount={portCount}
                  error={
                    errors.incotermPortType || errors.incotermMultiPortType
                  }
                  onPortSetup={onPortSetup}
                />
                <CounterDischargePorts
                  formData={formData}
                  portCount={portCount}
                  showQuantity={false}
                  incotermLocationOptions={incotermLocationOptions}
                  onFormChange={onFormChange}
                  errors={errors}
                />
              </>
            ) : null}

            {requiresCargoRatio ? (
              <FormField
                group
                label='Cargo ratio'
                name='selectedRatios'
                required
                hint='Select one or more cargo ratios'
                error={errors.selectedRatios}
                className='mt-3'
              >
                <div className='cargo-ratio-selector border rounded-3 p-3 bg-light-subtle'>
                  <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
                    {RATIO_OPTIONS.map(option => {
                      const isSelected = isRatioSelected(
                        selectedRatios,
                        option
                      );

                      return (
                        <button
                          key={option.id}
                          type='button'
                          aria-pressed={isSelected}
                          className={`btn btn-sm px-3 py-2 border rounded-3 d-flex d-md-inline-flex align-items-center gap-2 w-100 w-md-auto transition-all ${
                            isSelected
                              ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                              : 'border-secondary-subtle bg-white text-dark shadow-sm'
                          }`}
                          onClick={() => handleToggleRatio(option)}
                        >
                          <span
                            className={`rounded-circle border d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                              isSelected
                                ? 'border-primary bg-primary text-white'
                                : 'border-secondary bg-light'
                            }`}
                            style={{ width: 18, height: 18 }}
                          >
                            {isSelected ? (
                              <i className='ri-check-line fs-14 lh-1'></i>
                            ) : null}
                          </span>
                          <span className='fw-medium text-start'>
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </FormField>
            ) : null}
          </>
        )}
      </ModalBody>

      <CounterModalFooter
        loading={loading}
        saveDisabled={saveDisabled}
        onCancel={toggle}
        onSave={onSave}
        formData={formData}
      />
    </Modal>
  );
};

export default CounterOfferModalLpg;
